/**
 * Mount del tablón de NOTAMs `/notams.html` — Fase 10a.1 + 10a.2.
 *
 * Render de la pizarra con post-its read-only (10a.1) + interacción
 * completa (10a.2):
 *  - Botón "+ NUEVO POST-IT" en ACCIONES sidebar.
 *  - Click sobre un post-it → expand-to-read; si eres autor o admin+
 *    aparecen botones EDITAR/BORRAR dentro del expanded.
 *  - Drag&drop sobre post-its propios (autor o admin+); el backend
 *    PATCH valida que un admin no autor solo pueda mover posición.
 *
 * El cierre del expand al click fuera del post-it sigue siendo
 * automático. El modal de editor reusa estilo de confirm-modal.
 */

import {
  hasRole,
  type AuthenticatedPilot,
  type Notam,
  type NotamPostit,
  type PilotSummary,
  type Role,
} from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import { fetchAppSettings } from '../settings/api.js';
import {
  createNotam,
  createNotamPostit,
  deleteNotam,
  deleteNotamPostit,
  fetchAllNotams,
  fetchNotamBoard,
  fetchPilots,
  updateNotam,
  updateNotamPostit,
} from './api.js';
import { openArchivedModal } from './archived-modal.js';
import { setupPostitDragDrop } from './drag.js';
import { openPostitEditor } from './editor.js';
import { openNotamEditor } from './notam-editor.js';
import {
  applyDiscordSettings,
  renderError,
  renderNotamsActions,
  renderNotamsShell,
  renderPostits,
  renderSheet,
} from './markup.js';

import './notams.css';

/**
 * Estado en memoria del módulo. Mantenemos la lista actual de post-its
 * y el catálogo de pilotos en variables compartidas porque las
 * operaciones (crear/editar/borrar/mover) leen y mutan ambos sin
 * pasar por una store dedicada — el ámbito de la página es chico.
 */
interface PageState {
  contentRoot: HTMLElement;
  /** Usuario autenticado, o `null` si la página se montó para un anónimo. */
  user: AuthenticatedPilot | null;
  pilots: PilotSummary[];
  notams: Notam[];
  postits: NotamPostit[];
}

export async function mountNotams(): Promise<void> {
  // Página pública: usuarios anónimos pueden ver el tablón. `user` queda
  // `null` para anónimos — todas las afford­ances de creación/edición/
  // borrado se gatean luego con `user !== null`.
  const { contentRoot, user } = await mountChrome({
    activeSection: 'notams',
    allowAnonymous: true,
  });
  contentRoot.innerHTML = renderNotamsShell();
  replaceActionsSidebar(renderNotamsActions(user));

  // Settings de Discord (Fase 11g): si hay valores en BD los aplicamos
  // sobre el `<a class="notams-discord">` recién pintado. Fire-and-
  // forget; si el fetch falla, el shell ya tiene los defaults.
  void fetchAppSettings().then((res) => {
    if (res.ok) applyDiscordSettings(contentRoot, res.data.discord);
  });

  const boardResult = await fetchNotamBoard();
  if (!boardResult.ok) {
    showError(contentRoot, boardResult.message);
    return;
  }

  // Pilotos para el dropdown de destinatario. Si falla, seguimos con
  // lista vacía (el editor sigue funcionando, solo sin sugerencias).
  const pilotsResult = await fetchPilots();
  const pilots = pilotsResult.ok ? pilotsResult.data : [];

  const state: PageState = {
    contentRoot,
    user,
    pilots,
    notams: boardResult.data.notams,
    postits: boardResult.data.postits,
  };

  renderSheetInto(state);
  renderPostitsInto(state);
  wirePostitInteractions(state);
  wireDragDrop(state);
  wireActionsSidebar(state);
  wireSheetInteractions(state);
}

// ─── Render helpers ──────────────────────────────────────────────────

function renderSheetInto(state: PageState): void {
  const sheet = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="sheet"]',
  );
  if (sheet) sheet.innerHTML = renderSheet(state.notams, state.user);
}

function renderPostitsInto(state: PageState): void {
  const layer = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="postits"]',
  );
  if (layer) layer.innerHTML = renderPostits(state.postits, state.user);
}

function showError(root: HTMLElement, message: string): void {
  const stage = root.querySelector<HTMLElement>('.notams-stage');
  if (stage) stage.insertAdjacentHTML('beforeend', renderError(message));
}

function replaceActionsSidebar(html: string): void {
  const host = document.querySelector<HTMLElement>(
    '.chrome-sidebar--right .chrome-actions',
  );
  if (host) host.outerHTML = html;
}

// ─── Click expand + EDITAR/BORRAR ────────────────────────────────────

/**
 * Click sobre un post-it = expand para leer; reclick o click fuera lo
 * colapsa. Dentro del expanded, los botones EDITAR/BORRAR (cuando
 * existen) lanzan los flujos correspondientes.
 *
 * Un solo post-it expandido a la vez para no convertir el corcho en
 * una marabunta de paneles.
 */
function wirePostitInteractions(state: PageState): void {
  const layer = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="postits"]',
  );
  if (!layer) return;

  const collapseAll = (): void => {
    layer
      .querySelectorAll<HTMLElement>('.notams-postit--expanded')
      .forEach((el) => el.classList.remove('notams-postit--expanded'));
  };

  layer.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const actionBtn = target.closest<HTMLElement>('[data-postit-action]');
    if (actionBtn) {
      ev.stopPropagation();
      const postit = actionBtn.closest<HTMLElement>('.notams-postit');
      if (!postit) return;
      const id = postit.dataset['postitId'];
      if (!id) return;
      const action = actionBtn.dataset['postitAction'];
      if (action === 'edit') void handleEdit(state, id);
      else if (action === 'delete') void handleDelete(state, id);
      return;
    }

    const postit = target.closest<HTMLElement>('.notams-postit');
    if (!postit) return;
    const wasExpanded = postit.classList.contains('notams-postit--expanded');
    collapseAll();
    if (!wasExpanded) postit.classList.add('notams-postit--expanded');
  });

  // Click fuera de cualquier post-it → colapsar todos.
  state.contentRoot.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    if (!target.closest('.notams-postit')) collapseAll();
  });

  wireDiscordPin(state.contentRoot);
}

// ─── Drag&drop ───────────────────────────────────────────────────────

function wireDragDrop(state: PageState): void {
  const stage = state.contentRoot.querySelector<HTMLElement>('.notams-stage');
  if (!stage) return;

  setupPostitDragDrop({
    stage,
    onDrop: async (postitId, xPercent, yPercent) => {
      const existing = state.postits.find((p) => p.id === postitId);
      // Optimismo: ya hemos actualizado visualmente el post-it durante el
      // drag. Si el PATCH falla, revertimos la posición desde el server.
      const res = await updateNotamPostit(postitId, { xPercent, yPercent });
      if (!res.ok) {
        showError(state.contentRoot, `No se pudo mover: ${res.message}`);
        if (existing) {
          // Revertir visual al state previo (los % en BD).
          await reloadBoard(state);
        }
        return;
      }
      // Reemplazar el post-it en estado local con el del server.
      state.postits = state.postits.map((p) => (p.id === postitId ? res.data : p));
    },
  });
}

// ─── ACCIONES sidebar: + NUEVO POST-IT ───────────────────────────────

function wireActionsSidebar(state: PageState): void {
  const newPostitBtn = document.querySelector<HTMLButtonElement>(
    '[data-notams-action="new-postit"]',
  );
  newPostitBtn?.addEventListener('click', () => {
    void handleNewPostit(state);
  });

  // El botón "+ NUEVA NOTAM" solo se renderiza para admin+, así que aquí
  // basta con buscarlo: si no está, simplemente no enganchamos.
  const newNotamBtn = document.querySelector<HTMLButtonElement>(
    '[data-notams-action="new-notam"]',
  );
  newNotamBtn?.addEventListener('click', () => {
    void handleNewNotam(state);
  });

  const archivedBtn = document.querySelector<HTMLButtonElement>(
    '[data-notams-action="archived"]',
  );
  archivedBtn?.addEventListener('click', () => {
    void handleArchived(state);
  });
}

/**
 * Abre el modal "ARCHIVADOS" con la lista de NOTAMs archivados o
 * caducados. Si tras cerrar se ha restaurado al menos uno, refresca el
 * board para que aparezca en la hoja.
 */
async function handleArchived(state: PageState): Promise<void> {
  const all = await fetchAllNotams();
  if (!all.ok) {
    showError(state.contentRoot, `No se pudo cargar el archivo: ${all.message}`);
    return;
  }
  // Mostrar solo archivados o caducados (los activos viven en la hoja).
  const now = new Date().toISOString();
  const archived = all.data.filter((n) => n.archived || n.expiresAt <= now);

  let didChange = false;
  await openArchivedModal(archived, {
    onRestore: async (id) => {
      const res = await updateNotam(id, { archived: false });
      if (!res.ok) {
        showError(state.contentRoot, `No se pudo restaurar: ${res.message}`);
        return false;
      }
      didChange = true;
      return true;
    },
    onDelete: async (id) => {
      const res = await deleteNotam(id);
      if (!res.ok) {
        showError(state.contentRoot, `No se pudo borrar: ${res.message}`);
        return false;
      }
      didChange = true;
      return true;
    },
  });

  if (didChange) {
    // Refrescar el state local con lo que vea ahora /board (incluye
    // los recién restaurados que no han caducado).
    const board = await fetchNotamBoard();
    if (board.ok) {
      state.notams = board.data.notams;
      renderSheetInto(state);
    }
  }
}

/**
 * Click sobre un párrafo de la hoja oficial. Solo admin+ tiene los
 * items marcados como `--editable` y con `role="button"`. El handler
 * abre el editor en modo edit para ese NOTAM.
 *
 * Aceptamos también Enter/Espacio (los items son `tabindex="0"`).
 */
function wireSheetInteractions(state: PageState): void {
  // Anónimo o no-admin: la hoja es de solo lectura, no se cablea la
  // apertura del editor de NOTAMs.
  if (!state.user || !hasRole(state.user.role as Role, 'admin')) return;
  const sheet = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="sheet"]',
  );
  if (!sheet) return;

  const open = (target: HTMLElement): void => {
    const item = target.closest<HTMLElement>('.notams-sheet-item--editable');
    if (!item) return;
    const id = item.dataset['notamId'];
    if (!id) return;
    void handleEditNotam(state, id);
  };
  sheet.addEventListener('click', (ev) => open(ev.target as HTMLElement));
  sheet.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const t = ev.target as HTMLElement;
    if (!t.classList?.contains('notams-sheet-item--editable')) return;
    ev.preventDefault();
    open(t);
  });
}

async function handleNewPostit(state: PageState): Promise<void> {
  // Guard defensivo: para anónimos el botón "+ NUEVO POST-IT" no se
  // renderiza, así que este handler nunca se cablea. El check mantiene
  // el tipo estrecho para acceder a `state.user.id`.
  if (!state.user) return;
  // Posición inicial en la esquina superior izquierda del área de
  // madera (X 14-95%, Y 17-93%). El 50%/50% original caía encima de la
  // hoja central — el usuario tenía que moverlo siempre. 18%/20% está
  // en zona libre, por encima de la hoja y a la izquierda.
  const result = await openPostitEditor({
    mode: 'create',
    pilots: state.pilots,
    currentUserId: state.user.id,
    initial: { xPercent: 18, yPercent: 20 },
  });
  if (!result) return;
  const res = await createNotamPostit(result);
  if (!res.ok) {
    showError(state.contentRoot, `No se pudo crear: ${res.message}`);
    return;
  }
  state.postits = [res.data, ...state.postits];
  renderPostitsInto(state);
}

// ─── CRUD de NOTAMs hoja (solo admin+) ───────────────────────────────

async function handleNewNotam(state: PageState): Promise<void> {
  const result = await openNotamEditor({ mode: 'create' });
  if (!result || result.action !== 'save') return;
  const res = await createNotam(result.payload);
  if (!res.ok) {
    showError(state.contentRoot, `No se pudo crear: ${res.message}`);
    return;
  }
  // Si el admin lo creó ya archivado, no aparece en el tablón — no lo
  // metemos en el state local. Solo se verá en el modal "ARCHIVADOS".
  if (res.data.archived) return;
  // Insertar al principio (la hoja ordena publishedAt desc).
  state.notams = [res.data, ...state.notams];
  renderSheetInto(state);
}

async function handleEditNotam(state: PageState, notamId: string): Promise<void> {
  const existing = state.notams.find((n) => n.id === notamId);
  if (!existing) return;
  const result = await openNotamEditor({
    mode: 'edit',
    initial: { body: existing.body, archived: existing.archived },
  });
  if (!result) return;
  if (result.action === 'delete') {
    const ok = await openConfirmModal({
      title: 'Borrar NOTAM',
      message: '¿Seguro que quieres borrar este NOTAM? No se puede deshacer.',
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    const delRes = await deleteNotam(notamId);
    if (!delRes.ok) {
      showError(state.contentRoot, `No se pudo borrar: ${delRes.message}`);
      return;
    }
    state.notams = state.notams.filter((n) => n.id !== notamId);
    renderSheetInto(state);
    return;
  }
  // action === 'save'
  const upRes = await updateNotam(notamId, result.payload);
  if (!upRes.ok) {
    showError(state.contentRoot, `No se pudo guardar: ${upRes.message}`);
    return;
  }
  // Si quedó archivado, se filtra del board — quitarlo del array local.
  if (upRes.data.archived) {
    state.notams = state.notams.filter((n) => n.id !== notamId);
  } else {
    state.notams = state.notams.map((n) => (n.id === notamId ? upRes.data : n));
  }
  renderSheetInto(state);
}

async function handleEdit(state: PageState, postitId: string): Promise<void> {
  // Guard defensivo: el botón EDITAR de un post-it solo se renderiza
  // para su autor (siempre logueado). Estrecha el tipo de `state.user`.
  if (!state.user) return;
  const existing = state.postits.find((p) => p.id === postitId);
  if (!existing) return;
  const result = await openPostitEditor({
    mode: 'edit',
    pilots: state.pilots,
    currentUserId: state.user.id,
    initial: {
      body: existing.body,
      targetPilotId: existing.targetPilotId,
      visibility: existing.visibility,
      xPercent: existing.xPercent,
      yPercent: existing.yPercent,
    },
  });
  if (!result) return;
  const res = await updateNotamPostit(postitId, {
    body: result.body,
    targetPilotId: result.targetPilotId,
    visibility: result.visibility,
  });
  if (!res.ok) {
    showError(state.contentRoot, `No se pudo guardar: ${res.message}`);
    return;
  }
  state.postits = state.postits.map((p) => (p.id === postitId ? res.data : p));
  renderPostitsInto(state);
}

async function handleDelete(state: PageState, postitId: string): Promise<void> {
  const existing = state.postits.find((p) => p.id === postitId);
  if (!existing) return;
  const ok = await openConfirmModal({
    title: 'Borrar post-it',
    message: `¿Seguro que quieres borrar este post-it? Esta acción no se puede deshacer.`,
    confirmLabel: 'Borrar',
    danger: true,
  });
  if (!ok) return;
  const res = await deleteNotamPostit(postitId);
  if (!res.ok) {
    showError(state.contentRoot, `No se pudo borrar: ${res.message}`);
    return;
  }
  state.postits = state.postits.filter((p) => p.id !== postitId);
  renderPostitsInto(state);
}

async function reloadBoard(state: PageState): Promise<void> {
  const res = await fetchNotamBoard();
  if (!res.ok) {
    showError(state.contentRoot, res.message);
    return;
  }
  state.postits = res.data.postits;
  renderPostitsInto(state);
}

// ─── Discord pin deep-link ───────────────────────────────────────────

/**
 * Click sobre el pin de Discord: intenta abrir la app de escritorio
 * (URI `discord://`) y, si no responde en ~1.5s, abre la versión web
 * en pestaña nueva. La estrategia:
 *  - Lanzamos navegación al deep-link como `window.location`.
 *  - Programamos un timeout que abre la web si seguimos en la pestaña.
 *  - Si el usuario salta a la app, la pestaña pierde foco → cancelamos
 *    el timeout (`visibilitychange`/`blur`).
 *
 * Si el deep-link no tiene handler registrado, Chrome muestra un
 * diálogo "¿Abrir Discord?" — al cancelar, la pestaña sigue activa y
 * el fallback web salta a los 1.5s.
 */
function wireDiscordPin(root: HTMLElement): void {
  const link = root.querySelector<HTMLAnchorElement>('.notams-discord');
  if (!link) return;
  const appUrl = link.dataset['discordApp'];
  const webUrl = link.dataset['discordWeb'];
  if (!appUrl || !webUrl) return;

  link.addEventListener('click', (ev) => {
    ev.preventDefault();

    let fired = false;
    const fallback = window.setTimeout(() => {
      if (fired) return;
      fired = true;
      window.open(webUrl, '_blank', 'noopener,noreferrer');
      cleanup();
    }, 1500);

    const cancelFallback = (): void => {
      if (fired) return;
      fired = true;
      window.clearTimeout(fallback);
      cleanup();
    };
    const onVis = (): void => {
      if (document.hidden) cancelFallback();
    };
    const cleanup = (): void => {
      window.removeEventListener('blur', cancelFallback);
      document.removeEventListener('visibilitychange', onVis);
    };
    window.addEventListener('blur', cancelFallback);
    document.addEventListener('visibilitychange', onVis);

    window.location.href = appUrl;
  });
}
